import type {
  TreeMapViewConfig,
  BaseViewConfig,
  TreeMapColumnState,
  LevelDisplayOverride,
} from 'types/contextView';
import type { ContextLevel } from 'types/context';

export const TREEMAP_DEFAULT_VIEW_CONFIG: TreeMapViewConfig = {
  type: 'treemap',
  // `defaultCardView` is deliberately absent. It sits ahead of
  // `baseConfig.cardDensity` in resolveLevelConfig's precedence chain, so
  // seeding it with 'standard' here would make it *always* defined and render
  // the `cardDensity` link unreachable — silently ignoring the density the
  // seed migrations write to `base_config.cardDensity`. The 'standard' default
  // belongs at the end of that chain, and that is where it lives.
  allowCardViewToggle: true,
  columnWidth: 320,
  columnGap: 16,
  allowDragDrop: true,
  showMinimap: false,
};

export const TREEMAP_DEFAULT_BASE_CONFIG: BaseViewConfig = {
  cardDensity: 'standard',
  showStatus: true,
  showFlow: true,
  showAvatar: true,
  visibleProperties: ['description'],
};

/** Effective per-column display config returned by {@link resolveLevelConfig}. */
export interface ResolvedLevelConfig {
  cardDensity: 'minimal' | 'standard' | 'expanded';
  visibleProperties: string[];
  showStatus: boolean;
  showFlow: boolean;
  showAvatar: boolean;
}

/**
 * How many entries of the author's ordered `visibleProperties` list render at
 * each density. Shared by every card renderer that consumes
 * `ResolvedLevelConfig.visibleProperties` (`TreeMapCard`, `TreeMapGridNode`)
 * so the two renderers can't silently drift apart.
 */
export const VISIBLE_PROPERTIES_LIMIT_BY_VIEW: Record<string, number> = {
  minimal: 1,
  standard: 3,
  expanded: 8,
};

/** Reserved `levelOverrides` / `columnState` key for the focus/root column, which has no `ContextLevel`. */
export const ROOT_COLUMN_LEVEL_ID = '__root__';

/** Column width bounds (px) enforced on both write (resize/hydrate) and the editor's numeric input. */
export const COLUMN_WIDTH_MIN = 100;
export const COLUMN_WIDTH_MAX = 800;

/** Clamps a column width to `[COLUMN_WIDTH_MIN, COLUMN_WIDTH_MAX]`. */
export function clampColumnWidth(width: number): number {
  return Math.max(COLUMN_WIDTH_MIN, Math.min(COLUMN_WIDTH_MAX, width));
}

/** Maximum number of collapsed-node ids persisted to `viewConfig.collapsedNodeIds`. */
export const MAX_COLLAPSED_NODE_IDS = 500;

/** Caps a collapsed-node id list to {@link MAX_COLLAPSED_NODE_IDS}, keeping the most recently added ids. */
export function capCollapsedNodeIds(ids: string[]): string[] {
  return ids.length > MAX_COLLAPSED_NODE_IDS ? ids.slice(-MAX_COLLAPSED_NODE_IDS) : ids;
}

/**
 * Filters a stored collapsed-node id list down to the ids present in the
 * current result set. Ids for spheres no longer in view are dropped from the
 * *render* set only — callers must keep the original stored list intact so a
 * sphere that reappears (e.g. after a filter change) is still collapsed.
 */
export function pruneCollapsedNodeIds(
  storedIds: string[] | undefined,
  currentResultSetIds: Set<string>
): Set<string> {
  const pruned = new Set<string>();
  (storedIds ?? []).forEach((id) => {
    if (currentResultSetIds.has(id)) pruned.add(id);
  });
  return pruned;
}

/** View-level page size (`viewConfig.defaultPageSize`) wins over the component's `defaultPageSize` prop. */
export function resolveEffectivePageSize(viewConfig: TreeMapViewConfig, fallback: number): number {
  return viewConfig.defaultPageSize ?? fallback;
}

/**
 * Expand context levels by repeating each level according to its `repeat` property.
 * E.g., [{label: 'Subject', repeat: 1}, {label: 'Sphere', repeat: 3}]
 * becomes [{label: 'Subject'}, {label: 'Sphere'}, {label: 'Sphere'}, {label: 'Sphere'}]
 */
export function expandContextLevels(levels: ContextLevel[]): ContextLevel[] {
  if (!levels || levels.length === 0) return [];

  const expanded: ContextLevel[] = [];
  for (const level of levels) {
    const repeatCount = Math.min(Math.max(level.repeat || 1, 1), 5);
    for (let i = 0; i < repeatCount; i++) {
      expanded.push(level);
    }
  }
  return expanded;
}

/**
 * Maps a rendered column index to the level id that keys `viewConfig.columnState`.
 *
 * Column 0 is always the root/focus column. Columns 1+ index into the *expanded*
 * level list (post-`repeat`), offset by `rootTemplateLevel` — the template level
 * the focus column currently sits at when drilled down.
 *
 * Returns `null` when the column maps to no level. Callers MUST skip a `null`
 * rather than substitute `ROOT_COLUMN_LEVEL_ID`: an out-of-range column that
 * falls back to the root id writes the focus column's persisted entry, so the
 * wrong column is restored on reload and a junk `__root__` entry is left behind.
 */
export function getLevelIdForColumn(
  expandedLevels: ContextLevel[] | undefined,
  rootTemplateLevel: number,
  column: number
): string | null {
  if (column === 0) return ROOT_COLUMN_LEVEL_ID;
  return expandedLevels?.[rootTemplateLevel + column - 1]?.id ?? null;
}

/**
 * Merges two `Record<string, T>` maps by key, and — for each key present in
 * either side — merges the two entries' own fields rather than letting one
 * side's entry replace the other's wholesale. This is what makes
 * `mergeViewConfig`/`mergeBaseConfig` safe to call twice for two different
 * fields on the *same* level id (e.g. a resize then a hide on `lvl_a`)
 * without the second call silently dropping the first's field.
 *
 * `T extends object` rather than `Record<string, unknown>`: the entry types are
 * declared as `interface`s (`TreeMapColumnState`, `LevelDisplayOverride`), and a
 * TS interface gets no implicit index signature, so it does not satisfy
 * `Record<string, unknown>`. Spreading is all this function needs.
 *
 * Exported so `resolveViewConfig` (`lib/utils/resolveViewConfig.ts`) can reuse
 * this exact merge — a second copy of the EAINT-775 fix would silently diverge.
 */
export function mergeRecordByKey<T extends object>(
  base?: Record<string, T>,
  override?: Record<string, T>
): Record<string, T> | undefined {
  if (!base && !override) return undefined;
  const merged: Record<string, T> = {};
  for (const key of new Set([...Object.keys(base ?? {}), ...Object.keys(override ?? {})])) {
    merged[key] = { ...base?.[key], ...override?.[key] } as T;
  }
  return merged;
}

/**
 * Shallow-merge a partial TreeMapViewConfig onto the defaults.
 * `columnState` is merged per key AND per field within a key, so a patch
 * touching one level's width doesn't drop that level's already-stored
 * hidden flag (or vice versa), and a patch touching one level doesn't drop
 * every other level's entry.
 * Returns the base config if no override is provided.
 */
export function mergeViewConfig(
  base: TreeMapViewConfig,
  override?: Partial<TreeMapViewConfig>
): TreeMapViewConfig {
  if (!override) return base;
  return {
    ...base,
    ...override,
    type: 'treemap',
    columnState: mergeRecordByKey<TreeMapColumnState>(base.columnState, override.columnState),
  };
}

/**
 * Shallow-merge a partial BaseViewConfig onto the defaults.
 * `levelOverrides` is merged per key AND per field within a key — see
 * `mergeViewConfig` above for why that matters.
 * Returns the base config if no override is provided.
 */
export function mergeBaseConfig(
  base: BaseViewConfig,
  override?: Partial<BaseViewConfig>
): BaseViewConfig {
  if (!override) return base;
  return {
    ...base,
    ...override,
    levelOverrides: mergeRecordByKey<LevelDisplayOverride>(base.levelOverrides, override.levelOverrides),
  };
}

/**
 * Resolves the effective per-column display config for one level,
 * reading through the level's override, the view/base config, and
 * finally the built-in defaults. `resolved.base` / `resolved.view` must
 * already be merged — the shape matches `resolveViewConfig`'s output
 * (`lib/utils/resolveViewConfig.ts`) so callers can pass its result
 * directly instead of separately merging two configs themselves.
 */
export function resolveLevelConfig(
  resolved: { base: BaseViewConfig; view: TreeMapViewConfig },
  levelId: string
): ResolvedLevelConfig {
  const { base: mergedBaseConfig, view: mergedViewConfig } = resolved;
  const override = mergedBaseConfig.levelOverrides?.[levelId];

  return {
    cardDensity:
      override?.cardDensity ??
      mergedViewConfig.defaultCardView ??
      mergedBaseConfig.cardDensity ??
      'standard',
    visibleProperties:
      override?.visibleProperties ??
      mergedBaseConfig.visibleProperties ??
      TREEMAP_DEFAULT_BASE_CONFIG.visibleProperties ??
      [],
    showStatus: override?.showStatus ?? mergedBaseConfig.showStatus ?? true,
    showFlow: override?.showFlow ?? mergedBaseConfig.showFlow ?? true,
    showAvatar: override?.showAvatar ?? mergedBaseConfig.showAvatar ?? true,
  };
}
